import React, { useEffect, useState, Fragment } from 'react'
import styled from 'styled-components'
import InnerHTML from 'dangerously-set-html-content'
import { useBreakpoint } from '../../hooks'
import OptionsHOC from '../../hocs/OptionsHOC'

const Ad = styled.div`
  display: ${props => (props.show ? `flex` : `none`)};
  justify-content: center;
  align-items: center;
  min-height: ${props => (props.show ? `300px` : `0`)};
  min-width: ${props => (props.show ? `250px` : `0`)};
  margin-top: ${props => (props.show ? `20px` : `0`)};
`

/** Function to traverse the html doc and remove the injected script from Ad url */
const removeScripts = () => {
  const scriptList = document.querySelectorAll("script[type='text/javascript']")
  const convertedNodeList = Array.from(scriptList)
  const scriptsToRemove = convertedNodeList.filter(script =>
    /h6\.msn\.com\/(nativeads|bingna)/g.test(script.src)
  )
  if (scriptsToRemove.length > 0) {
    scriptsToRemove.forEach(s => s.parentNode.removeChild(s))
  }
}

const AirfindAd = ({ getOption, ...props }) => {
  const [adUrl, setAdUrl] = useState()
  const [html, setHtml] = useState('')
  const [isMobile] = useBreakpoint()
  const [error, setError] = useState()

  /** Retrieve two possible responsive options for customer overrides */
  const mobileUrl = getOption('ad.url_mobile')
  const desktopUrl = getOption('ad.url_desktop')

  /** Exit and prevent the component from mounting if there is no ad url to fetch */
  if (!mobileUrl || !desktopUrl) {
    return null
  }

  /** Set the external Call url to two options retrieved via getOption */
  useEffect(() => {
    if (isMobile) {
      setAdUrl(mobileUrl)
    } else {
      setAdUrl(desktopUrl)
    }
  }, [isMobile])

  /**
   * Function that calls external API with two params
   * @param _isMounted {boolean} flag to ensure mounting script doesn't trigger re render
   * @param _adUrl {string} the constructed url state variable
   */
  const fetchScript = async (_isMounted, _adUrl) => {
    /** pre-construct the call url with an encoded ua attached to the current browser */
    const url = `${_adUrl}&ua=${encodeURIComponent(navigator.userAgent)}`

    /** compose a proper request to handle unknowns in the api request  protocol */
    const request = new Request(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'text/html',
        'Access-Control-Allow-Origin': '*'
      },
      credentials: 'omit'
    })
    /**
     * attempt to catch any errors for an atypical text/html response body
     * response.json() will always produce an Unexpected token < in JSON at position 0
     * get the full html document, then make a complete copy of it as a cloned object
     * TODO find a way to trap volatile CORS errors from Airfind's server-side protocol
     */
    try {
      const res = await fetch(request)
      /** data is a complete <!DOCTYPE html> doc text string */
      const data = await res.text()
      /** text object */
      const textObject = JSON.parse(data)

      if (_isMounted && textObject.htmlcode) {
        setHtml(textObject.htmlcode)
      }
    } catch (exception) {
      /**
       * set errors to state
       * TODO log errors to Sentry
       */
      setError(
        new Map([
          [TypeError, 'There was a problem fetching the response.'],
          [SyntaxError, 'There was a problem parsing the response.'],
          [Error, exception.message]
        ]).get(exception.constructor)
      )
    }
  }

  /**
   * Effect hook with cleanup
   * Cleans up scripts that are attached to DOM by previous external script
   */
  useEffect(() => {
    let isMounted = true
    fetchScript(isMounted, adUrl)

    return () => {
      isMounted = false
      removeScripts()
    }
  }, [adUrl])

  return (
    <Ad {...props}>
      <InnerHTML id="airfind-ad" style={{ display: 'block' }} html={html} />
    </Ad>
  )
}

export default OptionsHOC(AirfindAd)



// WEBPACK FOOTER //
// ./src/components/AirfindAd/index.js